import type { Guest, Reservation, RestaurantConfig, Table, ZoneConfig } from "./types";
import { addHoursToTime, timeFromNow, todayStr } from "../utils/date";

const TODAY = todayStr();

// ── Restaurant config ────────────────────────────────────────────
export const seedRestaurantConfig: RestaurantConfig = {
  closeTime: "11:00 PM",
  name: "Seim·Seim — Bar Tai",
  noShowToleranceMinutes: 10,
  openTime: "12:00 PM",
  proximityMinutes: 60,
  timeLimitMinutes: 180,
};

// ── Zones (based on actual Seim·Seim floor plan) ────────────────
export const seedZones: ZoneConfig[] = [
  {
    borderColor: "#279400",
    color: "#e6ffe5",
    height: 106.8,
    id: "z100",
    name: "Terraza",
    width: 30.1,
    x: 106.2,
    y: -20.3,
  },
  {
    borderColor: "#818cf8",
    color: "#e0e7ff",
    height: 49,
    id: "z101",
    name: "Bar",
    width: 20.8,
    x: 94.6,
    y: 20.3,
  },
  {
    borderColor: "#e68600",
    color: "#f1ebe0",
    height: 41.1,
    id: "z102",
    name: "Barra",
    width: 56.5,
    x: 48.5,
    y: -26.6,
  },
  {
    borderColor: "#a1a1aa",
    color: "#e5e5e5",
    height: 13.8,
    id: "z103",
    isBlock: true,
    name: "Barra",
    width: 42.4,
    x: 50.8,
    y: -17.2,
  },
  {
    borderColor: "#0091c2",
    color: "#b6e3ec",
    height: 48.1,
    id: "z104",
    name: "Salón",
    width: 56.2,
    x: 36.9,
    y: 20.3,
  },
  {
    borderColor: "#a1a1aa",
    color: "#e5e5e5",
    height: 30,
    id: "z105",
    isBlock: true,
    name: "WC",
    width: 14.8,
    x: 20.8,
    y: 29.7,
  },
  {
    borderColor: "#a1a1aa",
    color: "#e5e5e5",
    height: 22,
    id: "z106",
    isBlock: true,
    name: "Cocina",
    width: 12.2,
    x: 34.6,
    y: -15.6,
  },
  {
    borderColor: "#a1a1aa",
    color: "#e5e5e5",
    height: 22,
    id: "z107",
    isBlock: true,
    name: "Cocina Fondo",
    width: 12.2,
    x: 20.8,
    y: -15.6,
  },
];

// ── Tables (mapped from actual Seim·Seim layout) ──────────────
export const seedTables: Table[] = [
  // ─ Terraza (z100) ─
  {
    minCapacity: 1,
    maxCapacity: 3,
    id: "1",
    name: "Mesa 1",
    neighbors: [],
    shape: "round",
    features: [],
    x: 125.7,
    y: 71.9,
    zoneId: "z100",
  },
  {
    minCapacity: 1,
    maxCapacity: 2,
    id: "2",
    name: "Mesa 2",
    neighbors: ["5"],
    shape: "square",
    features: [],
    x: 107.2,
    y: 76.6,
    zoneId: "z100",
  },
  {
    minCapacity: 1,
    maxCapacity: 2,
    id: "3",
    name: "Mesa 3",
    neighbors: ["4"],
    shape: "round",
    features: [],
    x: 116.5,
    y: 59.4,
    zoneId: "z100",
  },
  {
    minCapacity: 1,
    maxCapacity: 3,
    id: "4",
    name: "Mesa 4",
    neighbors: ["3"],
    shape: "round",
    features: [],
    x: 116.5,
    y: 50,
    zoneId: "z100",
  },
  {
    minCapacity: 1,
    maxCapacity: 2,
    id: "5",
    name: "Mesa 5",
    neighbors: ["2"],
    shape: "square",
    features: [],
    x: 114.2,
    y: 76.6,
    zoneId: "z100",
  },
  {
    minCapacity: 1,
    maxCapacity: 1,
    id: "6",
    name: "Mesa 6",
    neighbors: ["7"],
    shape: "square",
    features: [],
    x: 129.2,
    y: 57.8,
    zoneId: "z100",
  },
  {
    minCapacity: 1,
    maxCapacity: 1,
    id: "7",
    name: "Mesa 7",
    neighbors: ["6"],
    shape: "square",
    features: [],
    x: 129.2,
    y: 48.4,
    zoneId: "z100",
  },
  {
    minCapacity: 1,
    maxCapacity: 5,
    id: "8",
    name: "Mesa 8",
    neighbors: [],
    shape: "round",
    features: [],
    x: 126.7,
    y: 35.9,
    zoneId: "z100",
  },
  {
    minCapacity: 1,
    maxCapacity: 1,
    id: "14",
    name: "Mesa 14",
    neighbors: ["15"],
    shape: "square",
    features: [],
    x: 128,
    y: 17.2,
    zoneId: "z100",
  },
  {
    minCapacity: 1,
    maxCapacity: 1,
    id: "15",
    name: "Mesa 15",
    neighbors: ["14"],
    shape: "square",
    features: [],
    x: 128,
    y: 7.8,
    zoneId: "z100",
  },
  {
    minCapacity: 1,
    maxCapacity: 1,
    id: "16",
    name: "Mesa 16",
    neighbors: ["17"],
    shape: "square",
    features: [],
    x: 128,
    y: -4.7,
    zoneId: "z100",
  },
  {
    minCapacity: 1,
    maxCapacity: 1,
    id: "17",
    name: "Mesa 17",
    neighbors: ["16"],
    shape: "square",
    features: [],
    x: 128,
    y: -14.1,
    zoneId: "z100",
  },
  {
    minCapacity: 1,
    maxCapacity: 3,
    id: "18",
    name: "Mesa 18",
    neighbors: [],
    shape: "square",
    features: [],
    x: 110.8,
    y: 4.7,
    zoneId: "z100",
  },

  // ─ Bar (z101) ─
  {
    minCapacity: 1,
    maxCapacity: 2,
    id: "9",
    name: "Mesa 9",
    neighbors: ["10"],
    shape: "round",
    features: [],
    x: 96.9,
    y: 60.9,
    zoneId: "z101",
  },
  {
    minCapacity: 1,
    maxCapacity: 2,
    id: "10",
    name: "Mesa 10",
    neighbors: ["9"],
    shape: "round",
    features: [],
    x: 96.9,
    y: 51.6,
    zoneId: "z101",
  },
  {
    minCapacity: 1,
    maxCapacity: 2,
    id: "11",
    name: "Mesa 11",
    neighbors: ["12"],
    shape: "round",
    features: [],
    x: 96.9,
    y: 42.2,
    zoneId: "z101",
  },
  {
    minCapacity: 1,
    maxCapacity: 3,
    id: "12",
    name: "Mesa 12",
    neighbors: ["11"],
    shape: "round",
    features: [],
    x: 96.9,
    y: 32.8,
    zoneId: "z101",
  },
  {
    minCapacity: 1,
    maxCapacity: 3,
    id: "13",
    name: "Mesa 13",
    neighbors: [],
    shape: "round",
    features: [],
    x: 108.5,
    y: 26.6,
    zoneId: "z101",
  },

  // ─ Barra (z102) ─
  {
    minCapacity: 1,
    maxCapacity: 1,
    id: "19",
    name: "Mesa 19",
    neighbors: ["20"],
    shape: "round",
    features: [],
    x: 50.8,
    y: 3.1,
    zoneId: "z102",
  },
  {
    minCapacity: 1,
    maxCapacity: 1,
    id: "20",
    name: "Mesa 20",
    neighbors: ["19", "21"],
    shape: "round",
    features: [],
    x: 58.8,
    y: 3.1,
    zoneId: "z102",
  },
  {
    minCapacity: 1,
    maxCapacity: 1,
    id: "21",
    name: "Mesa 21",
    neighbors: ["20", "22"],
    shape: "round",
    features: [],
    x: 65.8,
    y: 3.1,
    zoneId: "z102",
  },
  {
    minCapacity: 1,
    maxCapacity: 1,
    id: "22",
    name: "Mesa 22",
    neighbors: ["23", "21"],
    shape: "round",
    features: [],
    x: 73.8,
    y: 3.1,
    zoneId: "z102",
  },
  {
    minCapacity: 1,
    maxCapacity: 1,
    id: "23",
    name: "Mesa 23",
    neighbors: ["22", "24"],
    shape: "round",
    features: [],
    x: 81.9,
    y: 3.1,
    zoneId: "z102",
  },
  {
    minCapacity: 1,
    maxCapacity: 1,
    id: "24",
    name: "Mesa 24",
    neighbors: ["23"],
    shape: "round",
    features: [],
    x: 88.8,
    y: 3.1,
    zoneId: "z102",
  },
  {
    minCapacity: 1,
    maxCapacity: 1,
    id: "25",
    name: "Mesa 25",
    neighbors: ["26"],
    shape: "round",
    features: [],
    x: 95.8,
    y: -3.1,
    zoneId: "z102",
  },
  {
    minCapacity: 1,
    maxCapacity: 1,
    id: "26",
    name: "Mesa 26",
    neighbors: ["25", "27"],
    shape: "round",
    features: [],
    x: 95.8,
    y: -12.5,
    zoneId: "z102",
  },
  {
    minCapacity: 1,
    maxCapacity: 1,
    id: "27",
    name: "Mesa 27",
    neighbors: ["26"],
    shape: "round",
    features: [],
    x: 95.8,
    y: -21.9,
    zoneId: "z102",
  },

  // ─ Salón (z104) ─
  {
    minCapacity: 1,
    maxCapacity: 2,
    id: "28",
    name: "Mesa 28",
    neighbors: ["29"],
    shape: "square",
    features: [],
    x: 84.2,
    y: 57.8,
    zoneId: "z104",
  },
  {
    minCapacity: 1,
    maxCapacity: 2,
    id: "29",
    name: "Mesa 29",
    neighbors: ["28"],
    shape: "square",
    features: [],
    x: 84.2,
    y: 48.4,
    zoneId: "z104",
  },
  {
    minCapacity: 1,
    maxCapacity: 3,
    id: "30",
    name: "Mesa 30",
    neighbors: ["31"],
    shape: "square",
    features: [],
    x: 84.2,
    y: 28.1,
    zoneId: "z104",
  },
  {
    minCapacity: 1,
    maxCapacity: 3,
    id: "31",
    name: "Mesa 31",
    neighbors: ["30"],
    shape: "square",
    features: [],
    x: 77.2,
    y: 28.1,
    zoneId: "z104",
  },
  {
    minCapacity: 1,
    maxCapacity: 3,
    id: "32",
    name: "Mesa 32",
    neighbors: ["33"],
    shape: "square",
    features: [],
    x: 69.2,
    y: 54.7,
    zoneId: "z104",
  },
  {
    minCapacity: 1,
    maxCapacity: 3,
    id: "33",
    name: "Mesa 33",
    neighbors: ["32"],
    shape: "square",
    features: [],
    x: 69.2,
    y: 43.8,
    zoneId: "z104",
  },
  {
    minCapacity: 1,
    maxCapacity: 3,
    id: "34",
    name: "Mesa 34",
    neighbors: ["35"],
    shape: "square",
    features: [],
    x: 55.4,
    y: 43.8,
    zoneId: "z104",
  },
  {
    minCapacity: 1,
    maxCapacity: 3,
    id: "35",
    name: "Mesa 35",
    neighbors: ["34"],
    shape: "square",
    features: [],
    x: 55.4,
    y: 54.7,
    zoneId: "z104",
  },
  {
    minCapacity: 1,
    maxCapacity: 6,
    id: "36",
    name: "Mesa 36",
    neighbors: [],
    shape: "square",
    features: [],
    x: 39.2,
    y: 40.6,
    zoneId: "z104",
  },
];

// ── Reservations (relative to NOW) ──────────────────────────────
// V2: All reservations enter as 'reservada'. No more 'pending', 'confirmed', 'risk'.

export function generateSeedReservations(): Reservation[] {
  // The seed is only used for local UI development before the API is
  // wired; the backend always returns a real ``pin_level``. Defaulting
  // to ``"none"`` keeps every row flexible so the rearrangement engine
  // treats the seed exactly like a fresh staff booking would.
  const rows: Omit<Reservation, "pinLevel">[] = [
    // ── Sentados (ocupando mesa) ────────────────────────────────
    // Carlos Ruiz: 5p → Mesa 29(2p)+30(3p) = 5p ✓
    {
      createdAt: Date.now() - 7200000,
      date: TODAY,
      endTime: addHoursToTime(timeFromNow(-58), 3),
      guestEmail: "cruiz@email.com",
      guestName: "Carlos Ruiz",
      guestPhone: "+57 318 555 4567",
      history: [
        {
          timestamp: Date.now() - 7200000,
          action: "created",
          detail: "Reserva creada · 5 pax · Web",
        },
        {
          timestamp: Date.now() - 3480000,
          action: "seated",
          detail: "Sentado en Mesa 29 + Mesa 30",
        },
      ],
      id: "r4",
      isConfirmedByHost: false,
      isVip: false,
      notes: "",
      origin: "web",
      pax: 5,
      preferredZone: "z104",
      seatedAt: timeFromNow(-58),
      status: "sentado",
      tableIds: ["29", "30"],
      tags: ["dairy_free"],
      features: [],
      internalNotes: "",
      time: timeFromNow(-60),
    },
    // Walk-in: 2p → Mesa 6(1p)+7(1p) = 2p ✓
    {
      createdAt: Date.now() - 3300000,
      date: TODAY,
      endTime: addHoursToTime(timeFromNow(-55), 3),
      guestEmail: "",
      guestName: "Walk-in",
      guestPhone: "",
      history: [
        { timestamp: Date.now() - 3300000, action: "walkin", detail: "Walk-in sentado · Mesa 6+7" },
      ],
      id: "w1",
      isConfirmedByHost: false,
      isVip: false,
      notes: "",
      origin: "walkin",
      pax: 2,
      preferredZone: "",
      seatedAt: timeFromNow(-55),
      status: "sentado",
      tableIds: ["6", "7"],
      tags: [],
      features: [],
      internalNotes: "",
      time: timeFromNow(-55),
    },
    // Daniel García: 3p → Mesa 4(3p) = 3p ✓
    {
      createdAt: Date.now() - 3000000,
      date: TODAY,
      endTime: addHoursToTime(timeFromNow(-50), 3),
      guestEmail: "",
      guestName: "Daniel García",
      guestPhone: "",
      history: [
        { timestamp: Date.now() - 3000000, action: "walkin", detail: "Walk-in sentado · Mesa 4" },
      ],
      id: "w2",
      isConfirmedByHost: false,
      isVip: false,
      notes: "",
      origin: "walkin",
      pax: 3,
      preferredZone: "",
      seatedAt: timeFromNow(-50),
      status: "sentado",
      tableIds: ["4"],
      tags: [],
      features: [],
      internalNotes: "",
      time: timeFromNow(-50),
    },
    // Camila Flores: 2p → Mesa 3(2p) = 2p ✓
    {
      createdAt: Date.now() - 4800000,
      date: TODAY,
      endTime: addHoursToTime(timeFromNow(-38), 3),
      guestEmail: "",
      guestName: "Camila Flores",
      guestPhone: "+57 330 3009182",
      history: [
        {
          timestamp: Date.now() - 4800000,
          action: "created",
          detail: "Reserva creada · 2 pax · Google",
        },
        { timestamp: Date.now() - 2280000, action: "seated", detail: "Sentado en Mesa 3" },
      ],
      id: "r-cf",
      isConfirmedByHost: false,
      isVip: false,
      notes: "",
      origin: "google",
      pax: 2,
      preferredZone: "z100",
      seatedAt: timeFromNow(-38),
      status: "sentado",
      tableIds: ["3"],
      tags: [],
      features: [],
      internalNotes: "",
      time: timeFromNow(-40),
    },
    // Walk-in: 6p → Mesa 32(3p)+33(3p) = 6p ✓
    {
      createdAt: Date.now() - 1200000,
      date: TODAY,
      endTime: addHoursToTime(timeFromNow(-20), 3),
      guestEmail: "",
      guestName: "Walk-in",
      guestPhone: "",
      history: [
        {
          timestamp: Date.now() - 1200000,
          action: "walkin",
          detail: "Walk-in sentado · Mesa 32+33",
        },
      ],
      id: "w4",
      isConfirmedByHost: false,
      isVip: false,
      notes: "",
      origin: "walkin",
      pax: 6,
      preferredZone: "",
      seatedAt: timeFromNow(-20),
      status: "sentado",
      tableIds: ["32", "33"],
      tags: [],
      features: [],
      internalNotes: "",
      time: timeFromNow(-20),
    },
    // Walk-in: 6p → Mesa 34(3p)+35(3p) = 6p ✓
    {
      createdAt: Date.now() - 900000,
      date: TODAY,
      endTime: addHoursToTime(timeFromNow(-15), 3),
      guestEmail: "",
      guestName: "Walk-in",
      guestPhone: "",
      history: [
        {
          timestamp: Date.now() - 900000,
          action: "walkin",
          detail: "Walk-in sentado · Mesa 34+35",
        },
      ],
      id: "w5",
      isConfirmedByHost: false,
      isVip: false,
      notes: "",
      origin: "walkin",
      pax: 6,
      preferredZone: "",
      seatedAt: timeFromNow(-15),
      status: "sentado",
      tableIds: ["34", "35"],
      tags: [],
      features: [],
      internalNotes: "",
      time: timeFromNow(-15),
    },

    // ── No-show ─────────────────────────────────────────────────
    // Ana López: 1p → Mesa 20(1p) = 1p ✓
    {
      createdAt: Date.now() - 5400000,
      date: TODAY,
      endTime: addHoursToTime(timeFromNow(-45), 3),
      guestEmail: "",
      guestName: "Ana López",
      guestPhone: "+57 315 555 9012",
      history: [
        {
          timestamp: Date.now() - 5400000,
          action: "created",
          detail: "Reserva creada · 1 pax · Google",
        },
        { timestamp: Date.now() - 2700000, action: "noshow", detail: "No-show" },
      ],
      id: "r3",
      isConfirmedByHost: false,
      isVip: false,
      notes: "",
      origin: "google",
      pax: 1,
      preferredZone: "z102",
      seatedAt: "",
      status: "noshow",
      tableIds: ["20"],
      tags: [],
      features: [],
      internalNotes: "",
      time: timeFromNow(-45),
    },

    // ── Reservadas (próximas) ───────────────────────────────────
    // Pedro Morales: 3p → Mesa 18(3p) = 3p ✓
    {
      createdAt: Date.now() - 600000,
      date: TODAY,
      endTime: addHoursToTime(timeFromNow(30), 3),
      guestEmail: "",
      guestName: "Pedro Morales",
      guestPhone: "+57 311 555 7890",
      history: [
        {
          timestamp: Date.now() - 600000,
          action: "created",
          detail: "Reserva creada · 3 pax · Llamada",
        },
      ],
      id: "r6",
      isConfirmedByHost: false,
      isVip: true,
      notes: "Prefiere terraza",
      origin: "phone",
      pax: 3,
      preferredZone: "z100",
      seatedAt: "",
      status: "reservada",
      tableIds: ["18"],
      tags: ["gluten_free"],
      features: [],
      internalNotes: "",
      time: timeFromNow(30),
    },
    // Familia Santos: 2p → Mesa 28(2p) = 2p ✓
    {
      createdAt: Date.now() - 300000,
      date: TODAY,
      endTime: addHoursToTime(timeFromNow(30), 3),
      guestEmail: "santos@email.com",
      guestName: "Familia Santos",
      guestPhone: "+57 305 555 2345",
      history: [
        {
          timestamp: Date.now() - 300000,
          action: "created",
          detail: "Reserva creada · 2 pax · Google",
        },
      ],
      id: "r7",
      isConfirmedByHost: false,
      isVip: false,
      notes: "",
      origin: "google",
      pax: 2,
      preferredZone: "z104",
      seatedAt: "",
      status: "reservada",
      tableIds: ["28"],
      tags: [],
      features: [],
      internalNotes: "",
      time: timeFromNow(30),
    },
    // Camila Morales: 5p → Mesa 8(5p) = 5p ✓
    {
      createdAt: Date.now() - 1800000,
      date: TODAY,
      endTime: addHoursToTime(timeFromNow(60), 3),
      guestEmail: "",
      guestName: "Camila Morales",
      guestPhone: "+57 359 3490934",
      history: [
        {
          timestamp: Date.now() - 1800000,
          action: "created",
          detail: "Reserva creada · 5 pax · Google",
        },
      ],
      id: "r-cm",
      isConfirmedByHost: false,
      isVip: false,
      notes: "",
      origin: "google",
      pax: 5,
      preferredZone: "z100",
      seatedAt: "",
      status: "reservada",
      tableIds: ["8"],
      tags: [],
      features: [],
      internalNotes: "",
      time: timeFromNow(60),
    },
    // Laura López: 2p → Mesa 11(2p) = 2p ✓
    {
      createdAt: Date.now() - 2400000,
      date: TODAY,
      endTime: addHoursToTime(timeFromNow(90), 3),
      guestEmail: "",
      guestName: "Laura López",
      guestPhone: "+57 320 8621825",
      history: [
        {
          timestamp: Date.now() - 2400000,
          action: "created",
          detail: "Reserva creada · 2 pax · WhatsApp",
        },
      ],
      id: "r-ll",
      isConfirmedByHost: false,
      isVip: false,
      notes: "",
      origin: "whatsapp",
      pax: 2,
      preferredZone: "z101",
      seatedAt: "",
      status: "reservada",
      tableIds: ["11"],
      tags: [],
      features: [],
      internalNotes: "",
      time: timeFromNow(90),
    },

    // ── Completadas ─────────────────────────────────────────────
    // Familia García: 2p → Mesa 5(2p) = 2p ✓
    {
      createdAt: Date.now() - 10800000,
      date: TODAY,
      endTime: addHoursToTime(timeFromNow(-118), 3),
      guestEmail: "garcia@email.com",
      guestName: "Familia García",
      guestPhone: "+57 310 555 1234",
      history: [
        {
          timestamp: Date.now() - 10800000,
          action: "created",
          detail: "Reserva creada · 2 pax · Seim·Seim",
        },
        { timestamp: Date.now() - 7080000, action: "seated", detail: "Sentado en Mesa 5" },
        { timestamp: Date.now() - 3600000, action: "completed", detail: "Comensal se retiró" },
      ],
      id: "r1",
      isConfirmedByHost: true,
      isVip: false,
      notes: "Cumpleaños",
      origin: "seimseim",
      pax: 2,
      preferredZone: "z100",
      seatedAt: timeFromNow(-118),
      status: "completada",
      tableIds: ["5"],
      tags: ["gluten_free"],
      features: [],
      internalNotes: "",
      time: timeFromNow(-120),
    },
    // Sr. Martínez: 2p → Mesa 10(2p) = 2p ✓
    {
      createdAt: Date.now() - 7200000,
      date: TODAY,
      endTime: addHoursToTime(timeFromNow(-88), 3),
      guestEmail: "martinez@vip.com",
      guestName: "Sr. Martínez",
      guestPhone: "+57 300 555 5678",
      history: [
        {
          timestamp: Date.now() - 7200000,
          action: "created",
          detail: "Reserva creada · 2 pax · Llamada",
        },
        { timestamp: Date.now() - 1800000, action: "completed", detail: "Comensal se retiró" },
      ],
      id: "r2",
      isConfirmedByHost: true,
      isVip: true,
      notes: "",
      origin: "phone",
      pax: 2,
      preferredZone: "z101",
      seatedAt: timeFromNow(-88),
      status: "completada",
      tableIds: ["10"],
      tags: [],
      features: [],
      internalNotes: "",
      time: timeFromNow(-90),
    },

    // ── Cancelada ───────────────────────────────────────────────
    // Dra. Fernández: 1p → Mesa 22(1p) = 1p ✓
    {
      createdAt: Date.now() - 3600000,
      date: TODAY,
      endTime: addHoursToTime(timeFromNow(15), 3),
      guestEmail: "",
      guestName: "Dra. Fernández",
      guestPhone: "+57 320 555 3456",
      history: [
        {
          timestamp: Date.now() - 3600000,
          action: "created",
          detail: "Reserva creada · 1 pax · WhatsApp",
        },
        {
          timestamp: Date.now() - 900000,
          action: "cancelled",
          detail: "Cancelada: Cancelado por staff",
        },
      ],
      id: "r5",
      isConfirmedByHost: true,
      isVip: false,
      notes: "",
      origin: "whatsapp",
      pax: 1,
      preferredZone: "z102",
      seatedAt: "",
      status: "cancelada",
      tableIds: ["22"],
      tags: [],
      features: [],
      internalNotes: "",
      time: timeFromNow(15),
    },
  ];
  return rows.map((r) => ({ ...r, pinLevel: "none" as const }));
}

export const seedGuests: Guest[] = [
  {
    attended: 28,
    avgPartySize: 3.5,
    cancellations: 4,
    clientSince: "Enero 2024",
    email: "garcia@email.com",
    favoriteTable: "Mesa 7 - Terraza",
    id: "g1",
    name: "Familia García",
    noShows: 2,
    notes: "Prefiere mesa junto a la ventana. Alergia a mariscos.",
    phone: "+57 310 555 1234",
    rating: 4.8,
    totalVisits: 34,
  },
  {
    attended: 50,
    avgPartySize: 4,
    cancellations: 2,
    clientSince: "Marzo 2023",
    email: "martinez@vip.com",
    favoriteTable: "Mesa 8",
    id: "g2",
    name: "Sr. Martínez",
    noShows: 0,
    notes: "Cliente VIP. Siempre pide la misma mesa.",
    phone: "+57 300 555 5678",
    rating: 5.0,
    totalVisits: 52,
  },
  {
    attended: 3,
    avgPartySize: 4,
    cancellations: 0,
    clientSince: "Noviembre 2025",
    email: "cruiz@email.com",
    favoriteTable: "",
    id: "g3",
    name: "Carlos Ruiz",
    noShows: 2,
    notes: "Historial de no-shows. Reservas paralelas frecuentes.",
    phone: "",
    rating: 3.2,
    totalVisits: 5,
  },
  // A–Z guest directory
  {
    attended: 11,
    avgPartySize: 2,
    cancellations: 1,
    clientSince: "Marzo 2025",
    email: "abotero@email.com",
    favoriteTable: "",
    id: "g-a",
    name: "Alejandra Botero",
    noShows: 0,
    notes: "",
    phone: "+57 311 200 1001",
    rating: 4.5,
    totalVisits: 12,
  },
  {
    attended: 7,
    avgPartySize: 4,
    cancellations: 0,
    clientSince: "Abril 2025",
    email: "bcastano@email.com",
    favoriteTable: "",
    id: "g-b",
    name: "Bernardo Castaño",
    noShows: 1,
    notes: "Alergia a maní.",
    phone: "+57 312 200 1002",
    rating: 4.0,
    totalVisits: 8,
  },
  {
    attended: 5,
    avgPartySize: 2,
    cancellations: 0,
    clientSince: "Mayo 2025",
    email: "cduarte@email.com",
    favoriteTable: "",
    id: "g-c",
    name: "Camila Duarte",
    noShows: 0,
    notes: "",
    phone: "+57 313 200 1003",
    rating: 4.8,
    totalVisits: 5,
  },
  {
    attended: 3,
    avgPartySize: 6,
    cancellations: 0,
    clientSince: "Junio 2025",
    email: "despinoza@email.com",
    favoriteTable: "",
    id: "g-d",
    name: "Diego Espinoza",
    noShows: 0,
    notes: "",
    phone: "+57 314 200 1004",
    rating: 4.2,
    totalVisits: 3,
  },
  {
    attended: 16,
    avgPartySize: 3,
    cancellations: 1,
    clientSince: "Febrero 2025",
    email: "efernandez@email.com",
    favoriteTable: "",
    id: "g-e",
    name: "Elena Fernández",
    noShows: 1,
    notes: "Vegana.",
    phone: "+57 315 200 1005",
    rating: 4.9,
    totalVisits: 18,
  },
  {
    attended: 20,
    avgPartySize: 4,
    cancellations: 2,
    clientSince: "Enero 2025",
    email: "fgomez@email.com",
    favoriteTable: "",
    id: "g-f",
    name: "Felipe Gómez",
    noShows: 0,
    notes: "",
    phone: "+57 316 200 1006",
    rating: 4.7,
    totalVisits: 22,
  },
  {
    attended: 2,
    avgPartySize: 2,
    cancellations: 0,
    clientSince: "Julio 2025",
    email: "gherrera@email.com",
    favoriteTable: "",
    id: "g-g",
    name: "Gabriela Herrera",
    noShows: 0,
    notes: "",
    phone: "+57 317 200 1007",
    rating: 5.0,
    totalVisits: 2,
  },
  {
    attended: 4,
    avgPartySize: 5,
    cancellations: 0,
    clientSince: "Agosto 2025",
    email: "hibarra@email.com",
    favoriteTable: "",
    id: "g-h",
    name: "Hugo Ibarra",
    noShows: 2,
    notes: "Historial de no-shows.",
    phone: "+57 318 200 1008",
    rating: 3.5,
    totalVisits: 6,
  },
  {
    attended: 9,
    avgPartySize: 3,
    cancellations: 0,
    clientSince: "Septiembre 2025",
    email: "ijaramillo@email.com",
    favoriteTable: "",
    id: "g-i",
    name: "Isabella Jaramillo",
    noShows: 0,
    notes: "",
    phone: "+57 319 200 1009",
    rating: 4.6,
    totalVisits: 9,
  },
  {
    attended: 4,
    avgPartySize: 2,
    cancellations: 0,
    clientSince: "Octubre 2025",
    email: "jkairuz@email.com",
    favoriteTable: "",
    id: "g-j",
    name: "Julián Kairuz",
    noShows: 0,
    notes: "",
    phone: "+57 320 200 1010",
    rating: 4.3,
    totalVisits: 4,
  },
  {
    attended: 6,
    avgPartySize: 4,
    cancellations: 1,
    clientSince: "Noviembre 2025",
    email: "klopez@email.com",
    favoriteTable: "",
    id: "g-k",
    name: "Karen López",
    noShows: 0,
    notes: "Sin gluten.",
    phone: "+57 321 200 1011",
    rating: 4.4,
    totalVisits: 7,
  },
  {
    attended: 14,
    avgPartySize: 6,
    cancellations: 0,
    clientSince: "Diciembre 2025",
    email: "lmedina@email.com",
    favoriteTable: "",
    id: "g-l",
    name: "Lorenzo Medina",
    noShows: 1,
    notes: "",
    phone: "+57 322 200 1012",
    rating: 4.1,
    totalVisits: 15,
  },
  {
    attended: 10,
    avgPartySize: 2,
    cancellations: 0,
    clientSince: "Enero 2026",
    email: "mnieto@email.com",
    favoriteTable: "",
    id: "g-m",
    name: "Mariana Nieto",
    noShows: 0,
    notes: "Cliente VIP.",
    phone: "+57 323 200 1013",
    rating: 5.0,
    totalVisits: 10,
  },
  {
    attended: 2,
    avgPartySize: 8,
    cancellations: 0,
    clientSince: "Febrero 2026",
    email: "nospina@email.com",
    favoriteTable: "",
    id: "g-n",
    name: "Nicolás Ospina",
    noShows: 1,
    notes: "",
    phone: "+57 324 200 1014",
    rating: 3.8,
    totalVisits: 3,
  },
  {
    attended: 1,
    avgPartySize: 2,
    cancellations: 0,
    clientSince: "Marzo 2026",
    email: "opardo@email.com",
    favoriteTable: "",
    id: "g-o",
    name: "Olivia Pardo",
    noShows: 0,
    notes: "",
    phone: "+57 325 200 1015",
    rating: 4.0,
    totalVisits: 1,
  },
  {
    attended: 5,
    avgPartySize: 4,
    cancellations: 1,
    clientSince: "Enero 2026",
    email: "pquintero@email.com",
    favoriteTable: "",
    id: "g-p",
    name: "Pablo Quintero",
    noShows: 0,
    notes: "",
    phone: "+57 326 200 1016",
    rating: 4.5,
    totalVisits: 6,
  },
  {
    attended: 2,
    avgPartySize: 3,
    cancellations: 0,
    clientSince: "Febrero 2026",
    email: "qramirez@email.com",
    favoriteTable: "",
    id: "g-q",
    name: "Quintina Ramírez",
    noShows: 0,
    notes: "",
    phone: "+57 327 200 1017",
    rating: 4.7,
    totalVisits: 2,
  },
  {
    attended: 10,
    avgPartySize: 5,
    cancellations: 1,
    clientSince: "Diciembre 2025",
    email: "rsalazar@email.com",
    favoriteTable: "",
    id: "g-r",
    name: "Ricardo Salazar",
    noShows: 0,
    notes: "Silla de bebé.",
    phone: "+57 328 200 1018",
    rating: 4.3,
    totalVisits: 11,
  },
  {
    attended: 13,
    avgPartySize: 2,
    cancellations: 0,
    clientSince: "Noviembre 2025",
    email: "storres@email.com",
    favoriteTable: "",
    id: "g-s",
    name: "Sofía Torres",
    noShows: 1,
    notes: "",
    phone: "+57 329 200 1019",
    rating: 4.9,
    totalVisits: 14,
  },
  {
    attended: 7,
    avgPartySize: 4,
    cancellations: 1,
    clientSince: "Octubre 2025",
    email: "turibe@email.com",
    favoriteTable: "",
    id: "g-t",
    name: "Tomás Uribe",
    noShows: 0,
    notes: "",
    phone: "+57 330 200 1020",
    rating: 4.2,
    totalVisits: 8,
  },
  {
    attended: 5,
    avgPartySize: 2,
    cancellations: 0,
    clientSince: "Septiembre 2025",
    email: "uvalencia@email.com",
    favoriteTable: "",
    id: "g-u",
    name: "Úrsula Valencia",
    noShows: 0,
    notes: "Mascota.",
    phone: "+57 331 200 1021",
    rating: 4.6,
    totalVisits: 5,
  },
  {
    attended: 18,
    avgPartySize: 3,
    cancellations: 1,
    clientSince: "Agosto 2025",
    email: "vwolff@email.com",
    favoriteTable: "",
    id: "g-v",
    name: "Valentina Wolff",
    noShows: 0,
    notes: "",
    phone: "+57 332 200 1022",
    rating: 4.8,
    totalVisits: 19,
  },
  {
    attended: 3,
    avgPartySize: 6,
    cancellations: 0,
    clientSince: "Julio 2025",
    email: "wximena@email.com",
    favoriteTable: "",
    id: "g-w",
    name: "William Ximena",
    noShows: 1,
    notes: "",
    phone: "+57 333 200 1023",
    rating: 3.9,
    totalVisits: 4,
  },
  {
    attended: 7,
    avgPartySize: 2,
    cancellations: 0,
    clientSince: "Junio 2025",
    email: "xyepes@email.com",
    favoriteTable: "",
    id: "g-x",
    name: "Ximena Yepes",
    noShows: 0,
    notes: "",
    phone: "+57 334 200 1024",
    rating: 5.0,
    totalVisits: 7,
  },
  {
    attended: 12,
    avgPartySize: 4,
    cancellations: 1,
    clientSince: "Mayo 2025",
    email: "yzapata@email.com",
    favoriteTable: "",
    id: "g-y",
    name: "Yamile Zapata",
    noShows: 0,
    notes: "",
    phone: "+57 335 200 1025",
    rating: 4.4,
    totalVisits: 13,
  },
  {
    attended: 8,
    avgPartySize: 3,
    cancellations: 0,
    clientSince: "Abril 2025",
    email: "zacosta@email.com",
    favoriteTable: "",
    id: "g-z",
    name: "Zamir Acosta",
    noShows: 1,
    notes: "",
    phone: "+57 336 200 1026",
    rating: 4.1,
    totalVisits: 9,
  },
];
